import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Building2, Trash2, Inbox, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, nuevas: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        api.get("/admin/submissions"),
        api.get("/admin/stats"),
      ]);
      setSubs(s.data);
      setStats(st.data);
    } catch {
      toast.error("No se pudieron cargar las solicitudes.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = "Panel — Asistentes Virtual";
    load();
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/submissions/${id}`);
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status: "read" } : s)));
      setStats((prev) => ({ ...prev, nuevas: Math.max(0, prev.nuevas - 1) }));
    } catch {
      toast.error("No se pudo actualizar.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/submissions/${id}`);
      setSubs((prev) => prev.filter((s) => s.id !== id));
      toast.success("Solicitud eliminada.");
      load();
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-brand-bgAlt" data-testid="admin-page">
      <header className="sticky top-0 z-10 border-b border-brand-line bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo size={26} />
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-[9px] border border-brand-line bg-white px-4 py-2 text-[14px] text-brand-ink hover:bg-brand-bgAlt transition-colors"
            >
              <RefreshCw size={15} /> Actualizar
            </button>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-[9px] bg-brand-ink px-4 py-2 text-[14px] font-medium text-white hover:-translate-y-0.5 transition-transform"
            >
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="text-3xl font-medium tracking-tight text-brand-ink">Solicitudes recibidas</h1>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 max-w-lg">
          <div className="rounded-[14px] border border-brand-line bg-white p-5" data-testid="stat-total">
            <div className="flex items-center gap-2 text-[13px] text-brand-gray">
              <Inbox size={16} /> Total
            </div>
            <p className="mt-1 text-3xl font-medium text-brand-ink">{stats.total}</p>
          </div>
          <div className="rounded-[14px] border border-brand-line bg-white p-5" data-testid="stat-nuevas">
            <div className="flex items-center gap-2 text-[13px] text-brand-gray">
              <span className="h-2 w-2 rounded-full bg-brand-mint" /> Nuevas
            </div>
            <p className="mt-1 text-3xl font-medium text-brand-indigo">{stats.nuevas}</p>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {loading ? (
            <p className="text-brand-gray">Cargando…</p>
          ) : subs.length === 0 ? (
            <div className="rounded-[14px] border border-dashed border-brand-line bg-white p-12 text-center text-brand-gray" data-testid="admin-empty">
              Todavía no hay solicitudes. Aparecerán aquí cuando alguien envíe el formulario.
            </div>
          ) : (
            subs.map((s) => (
              <div
                key={s.id}
                data-testid={`submission-${s.id}`}
                className={`rounded-[14px] border bg-white p-5 transition-colors ${
                  s.status === "new" ? "border-brand-indigo/40" : "border-brand-line"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[16px] font-medium text-brand-ink">{s.nombre}</span>
                      {s.status === "new" && (
                        <span className="rounded-full bg-brand-mint/15 px-2 py-0.5 text-[11px] font-medium text-[#085041]">
                          Nueva
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-[#5F5E5A]">
                      <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-indigo">
                        <Mail size={13} /> {s.email}
                      </a>
                      {s.empresa && (
                        <span className="inline-flex items-center gap-1.5">
                          <Building2 size={13} /> {s.empresa}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[12.5px] text-brand-gray">{formatDate(s.created_at)}</span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-[14px] leading-relaxed text-brand-ink">{s.mensaje}</p>
                <div className="mt-4 flex gap-2">
                  {s.status === "new" && (
                    <button
                      onClick={() => markRead(s.id)}
                      data-testid={`mark-read-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-[9px] border border-brand-line px-3 py-1.5 text-[13px] text-brand-ink hover:bg-brand-bgAlt transition-colors"
                    >
                      <CheckCircle2 size={14} /> Marcar como leída
                    </button>
                  )}
                  <button
                    onClick={() => remove(s.id)}
                    data-testid={`delete-${s.id}`}
                    className="inline-flex items-center gap-1.5 rounded-[9px] border border-brand-line px-3 py-1.5 text-[13px] text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={14} /> Eliminar
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
